import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { Select } from '@/components/ui/Select';
import { useAccess } from '@/features/access/access';
import './Settings.css';

type Config = {
  type: string;
  template: string;
  padding: number;
  reset: string;
  startAt: number;
  version: number;
};
const labels: Record<string, string> = {
  PROPOSITION: 'Propositions commerciales',
  BON_COMMANDE: 'Bons de commande',
  CONTRAT: 'Contrats',
  PVA: 'Procès-verbaux d’activité',
  FACTURE: 'Factures',
};
const key = ['document-numbering'];
const preview = (config: Config) =>
  config.template.replace(
    /{(YYYY|YY|MM|SEQ)}/g,
    (_, token: string) =>
      ({
        YYYY: String(new Date().getFullYear()),
        YY: String(new Date().getFullYear()).slice(-2),
        MM: String(new Date().getMonth() + 1).padStart(2, '0'),
        SEQ: String(config.startAt).padStart(config.padding, '0'),
      })[token] ?? '',
  );
export const DocumentNumberingPage = () => {
  const query = useQuery({
    queryKey: key,
    queryFn: () => httpClient.get<Config[]>('/settings/document-numbering'),
  });
  const access = useAccess();
  return (
    <div className="settings-page">
      <nav className="settings-breadcrumb">
        <Link to="/parametres">Paramètres</Link>
        <span> / Numérotation</span>
      </nav>
      <header className="settings-heading">
        <h1>Numérotation des documents</h1>
        <p>
          Personnalisez les références attribuées lors de l’émission. Les documents existants
          conservent leur numéro.
        </p>
      </header>
      <p className="settings-help">
        Utilisez {'{SEQ}'} pour le compteur, {'{YYYY}'} pour l’année et {'{MM}'} pour le mois.
        Exemple : INV{'{YYYY}'}-{'{SEQ}'} → INV2026-001.
      </p>
      {query.isPending ? (
        <p role="status">Chargement…</p>
      ) : query.isError ? (
        <p role="alert">{query.error.message}</p>
      ) : (
        <div className="numbering-list">
          {query.data.map((config) => (
            <NumberingForm
              key={config.type + config.version}
              initial={config}
              editable={access.data?.isAdmin ?? false}
            />
          ))}
        </div>
      )}
    </div>
  );
};
const NumberingForm = ({ initial, editable }: { initial: Config; editable: boolean }) => {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const cache = useQueryClient();
  const changed = JSON.stringify(value) !== JSON.stringify(initial);
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await httpClient.put('/settings/document-numbering', value);
      await cache.invalidateQueries({ queryKey: key });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="settings-preferences"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="settings-preferences-heading">
        <h2>{labels[value.type]}</h2>
        <span className="settings-scope">Documents générés</span>
      </div>
      <div className="numbering-fields">
        <TextField
          label="Format du numéro"
          value={value.template}
          disabled={!editable || saving}
          onChange={(event) => setValue({ ...value, template: event.target.value })}
        />
        <Select
          label="Nouvelle séquence"
          value={value.reset}
          disabled={!editable || saving}
          options={[
            { value: 'YEAR', label: 'Chaque année' },
            { value: 'MONTH', label: 'Chaque mois' },
            { value: 'NEVER', label: 'Continue' },
          ]}
          onChange={(event) => setValue({ ...value, reset: event.target.value })}
        />
        <TextField
          label="Nombre de chiffres"
          type="number"
          min={1}
          max={9}
          value={value.padding}
          disabled={!editable || saving}
          onChange={(event) => setValue({ ...value, padding: Number(event.target.value) })}
        />
        <TextField
          label="Premier numéro minimum"
          type="number"
          min={1}
          max={999999999}
          value={value.startAt}
          disabled={!editable || saving}
          onChange={(event) => setValue({ ...value, startAt: Number(event.target.value) })}
        />
      </div>
      <div className="numbering-preview">
        <span>Aperçu du format</span>
        <strong>{preview(value)}</strong>
        <small>Le compteur poursuit après le dernier numéro utilisé.</small>
      </div>
      {error && (
        <p role="alert" className="settings-notice settings-notice--error">
          {error}
        </p>
      )}
      {editable && (
        <footer className="settings-footer">
          <Button
            variant="secondary"
            disabled={!changed || saving}
            onClick={() => setValue(initial)}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={!changed} loading={saving}>
            Enregistrer
          </Button>
        </footer>
      )}
    </form>
  );
};
